import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  opsSupplierApi,
  type FingerprintConflict,
  type FingerprintConflictConclusion,
} from "@/api/opsSupplierClient";
import { ApiError } from "@/api/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Pagination } from "@/components/Pagination";
import { useTranslation } from "@/framework-originated/i18n";

type StatusFilter = "open" | "resolved" | "all";

/**
 * 台账 Note 的结构化视图:优先消费后端 List 接口的结构化字段,
 * 兜底解析 Note 文本(兼容未升级后端时的旧格式)。
 * null / undefined 语义:
 *  - submittedKey == null  → 历史冲突,台账未采集「提交的Key」(待补记)
 *  - submitterSites == null→ 历史冲突,台账未采集「目标站点」
 *  - submitterSites == []  → 新冲突,且仅上传、未发布到任何站点
 *  - conflictingKeys []    → 按指纹短码前缀没反查到同组既有 Key
 */
interface ConflictView {
  submitter: string;
  openedByName?: string;
  submittedKey: string | null;
  submitterSites: string[] | null;
  conflicting: string;
  fingerprint: string;
  conflictingKeys: NonNullable<FingerprintConflict["conflicting_keys"]>;
  conflictingSites: string[];
}

const noteRE =
  /提交方=([^;]*);既有归属=([^;]*);(?:提交的Key=([^;]*);)?(?:目标站点=([^;]*);)?指纹=([^;]*)/;

function parseNote(summary: string) {
  const m = noteRE.exec(summary);
  if (!m) return null;
  return {
    submitter: m[1]?.trim() || undefined,
    conflicting: m[2]?.trim() || undefined,
    submittedKey: m[3]?.trim() || undefined,
    targetSites:
      m[4] !== undefined
        ? m[4] === "无(仅上传)"
          ? []
          : m[4].split(",").map((s) => s.trim()).filter(Boolean)
        : undefined,
    fingerprint: m[5]?.trim() || undefined,
  };
}

function buildConflictView(item: FingerprintConflict): ConflictView {
  const note = parseNote(item.summary);
  const conflictingKeys = item.conflicting_keys ?? [];
  const sites = new Set<string>();
  for (const k of conflictingKeys) for (const s of k.sites ?? []) sites.add(s);
  return {
    submitter:
      item.submitter ??
      note?.submitter ??
      (item.opened_by_id != null ? `${item.opened_by_type}#${item.opened_by_id}` : item.summary),
    openedByName: item.opened_by_name ?? undefined,
    submittedKey: item.submitted_key ?? note?.submittedKey ?? null,
    submitterSites:
      item.target_sites !== undefined ? item.target_sites : note?.targetSites !== undefined ? note.targetSites : null,
    conflicting: item.conflicting ?? note?.conflicting ?? "—",
    fingerprint: item.fingerprint ?? note?.fingerprint ?? item.summary,
    conflictingKeys,
    conflictingSites: [...sites],
  };
}

const PAGE_SIZE_OPTIONS = [10, 20, 50];

const conclusions: FingerprintConflictConclusion[] = [
  "confirmed_existing_owner",
  "allow_submitter_retry",
  "no_conflict",
];

/** 解析裁决追加事件:conclusion=<enum>;reason=<自由文本(可含 ;)> */
function parseResolution(note?: string): { conclusion?: string; reason?: string } | null {
  if (!note) return null;
  const m = /^conclusion=([^;]*);reason=([\s\S]*)$/.exec(note);
  if (!m) return null;
  return { conclusion: m[1] || undefined, reason: m[2].trim() };
}

function fmtTime(value?: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
}

/** 数据可得性标签:✅ 现有数据可解析 / ⚠ 待补记 */
function Tag({ ok }: { ok: boolean }) {
  const { t } = useTranslation("opsSuppliers");
  return ok ? (
    <span className="text-[11px] font-normal text-emerald-600">✅ {t("conflicts.available")}</span>
  ) : (
    <span className="text-[11px] font-normal text-amber-600">⚠ {t("conflicts.pending")}</span>
  );
}

function Fact({ label, ok, value, hint }: { label: string; ok: boolean; value: string; hint?: string }) {
  return (
    <div className="rounded-lg border px-3 py-2.5">
      <h4 className="flex flex-wrap items-center gap-2 text-xs font-medium">
        {label} <Tag ok={ok} />
      </h4>
      <div className="mt-1 text-sm">{value}</div>
      {hint && <div className="mt-1 text-[11px] text-muted-foreground">{hint}</div>}
    </div>
  );
}

export function SupplierFingerprintConflictsPage() {
  const { t } = useTranslation("opsSuppliers");
  const qc = useQueryClient();
  const [status, setStatus] = useState<StatusFilter>("open");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selected, setSelected] = useState<FingerprintConflict | null>(null);
  const [conclusion, setConclusion] = useState<FingerprintConflictConclusion>("confirmed_existing_owner");
  const [reason, setReason] = useState("");
  const [message, setMessage] = useState<string | null>(null);

  const conflictsQ = useQuery({
    queryKey: ["supplier-fingerprint-conflicts", status, page, pageSize],
    queryFn: () => opsSupplierApi.listFingerprintConflicts({ status, page, page_size: pageSize }),
  });

  const resolveMut = useMutation({
    mutationFn: () => {
      if (!selected) throw new Error("no conflict selected");
      return opsSupplierApi.resolveFingerprintConflict(selected.conflict_ref, {
        conclusion,
        reason: reason.trim(),
      });
    },
    onSuccess: async () => {
      setMessage(t("conflicts.resolveSuccess"));
      setSelected(null);
      setReason("");
      await qc.invalidateQueries({ queryKey: ["supplier-fingerprint-conflicts"] });
    },
    onError: async (error) => {
      setMessage(error instanceof ApiError ? error.message : t("conflicts.resolveFailed"));
      if (error instanceof ApiError && error.status === 409) {
        setSelected(null);
        await qc.invalidateQueries({ queryKey: ["supplier-fingerprint-conflicts"] });
      }
    },
  });

  const items = conflictsQ.data?.items ?? [];
  const total = conflictsQ.data?.total ?? 0;

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{t("conflicts.title")}</h1>
          <p className="mt-1 max-w-2xl text-xs text-muted-foreground">{t("conflicts.description")}</p>
        </div>
        <select
          className="h-9 rounded-md border bg-background px-3 text-sm"
          value={status}
          onChange={(event) => {
            setStatus(event.target.value as StatusFilter);
            setPage(1);
            setSelected(null);
            setMessage(null);
          }}
        >
          <option value="open">{t("conflicts.filterOpen")}</option>
          <option value="resolved">{t("conflicts.filterResolved")}</option>
          <option value="all">{t("conflicts.filterAll")}</option>
        </select>
      </div>

      {message && <div className="rounded-md border px-3 py-2 text-sm">{message}</div>}

      <Card>
        <CardContent className="pt-6">
          {conflictsQ.isLoading && <div className="text-sm text-muted-foreground">…</div>}
          {conflictsQ.error && <div className="text-sm text-destructive">{String(conflictsQ.error)}</div>}
          {!conflictsQ.isLoading && items.length === 0 && (
            <div className="py-8 text-center text-sm text-muted-foreground">{t("conflicts.empty")}</div>
          )}
          {items.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="py-2">{t("conflicts.reference")}</th>
                    <th>{t("conflicts.status")}</th>
                    <th>{t("conflicts.submitter")}</th>
                    <th>{t("conflicts.submittedKey")}</th>
                    <th>{t("conflicts.submitterSite")}</th>
                    <th>{t("conflicts.conflicting")}</th>
                    <th>{t("conflicts.conflictingSites")}</th>
                    <th>{t("conflicts.openedAt")}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => {
                    const info = buildConflictView(item);
                    const res = parseResolution(item.resolution);
                    const resolutionText = res
                      ? `${res.conclusion ? t(`conflicts.conclusions.${res.conclusion}`) : ""}${
                          res.reason ? ` · ${res.reason}` : ""
                        }`
                      : item.resolution;
                    const pendingText = t("conflicts.pending");
                    const submitterSites = info.submitterSites
                      ? info.submitterSites.length > 0
                        ? info.submitterSites.join(", ")
                        : t("conflicts.uploadOnly")
                      : null;
                    return (
                      <tr key={item.conflict_ref} className="border-b align-top last:border-0">
                        <td className="py-3">
                          <span
                            title={item.conflict_ref}
                            className="block max-w-[150px] truncate font-mono text-xs"
                          >
                            {item.conflict_ref}
                          </span>
                        </td>
                        <td className="py-3">
                          <Badge variant={item.status === "open" ? "warning" : "success"}>
                            {t(`conflicts.${item.status}`)}
                          </Badge>
                        </td>
                        <td className="py-3">
                          <div className="max-w-[180px]">
                            {info.openedByName && (
                              <div className="truncate text-xs font-medium">{info.openedByName}</div>
                            )}
                            <div className="truncate font-mono text-xs text-muted-foreground">{info.submitter}</div>
                          </div>
                        </td>
                        <td className="py-3">
                          {info.submittedKey ? (
                            <span className="block max-w-[160px] truncate font-mono text-xs">{info.submittedKey}</span>
                          ) : (
                            <span className="text-xs text-amber-600">{pendingText}</span>
                          )}
                        </td>
                        <td className="py-3">
                          {submitterSites ? (
                            <span className="block max-w-[200px] truncate text-xs">{submitterSites}</span>
                          ) : (
                            <span className="text-xs text-amber-600">{pendingText}</span>
                          )}
                        </td>
                        <td className="py-3 font-mono text-xs">{info.conflicting}</td>
                        <td className="py-3">
                          {info.conflictingSites.length > 0 ? (
                            <span className="block max-w-[200px] truncate text-xs">
                              {info.conflictingSites.join(", ")}
                            </span>
                          ) : info.conflictingKeys.length === 0 ? (
                            <span className="text-xs text-muted-foreground">—</span>
                          ) : (
                            <span className="text-xs text-amber-600">{pendingText}</span>
                          )}
                        </td>
                        <td className="py-3 text-xs text-muted-foreground">{fmtTime(item.opened_at)}</td>
                        <td className="py-3 text-right">
                          {item.status === "open" ? (
                            <Button
                              size="sm"
                              onClick={() => {
                                setSelected(item);
                                setConclusion("confirmed_existing_owner");
                                setReason("");
                                setMessage(null);
                              }}
                            >
                              {t("conflicts.resolve")}
                            </Button>
                          ) : resolutionText ? (
                            <span
                              title={item.resolution}
                              className="block max-w-[240px] truncate text-xs text-muted-foreground"
                            >
                              {resolutionText}
                            </span>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <Pagination className="mt-4" page={page} total={conflictsQ.data?.total}
            pageSize={pageSize} pageSizeOptions={PAGE_SIZE_OPTIONS}
            onPageChange={setPage} onPageSizeChange={setPageSize} loading={conflictsQ.isFetching} />
        </CardContent>
      </Card>

      {selected && (
        <ResolveConflictModal
          conflict={selected}
          conclusion={conclusion}
          onConclusionChange={setConclusion}
          reason={reason}
          onReasonChange={setReason}
          pending={resolveMut.isPending}
          error={
            resolveMut.isError
              ? resolveMut.error instanceof ApiError
                ? resolveMut.error.message
                : t("conflicts.resolveFailed")
              : null
          }
          onConfirm={() => resolveMut.mutate()}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}

function ResolveConflictModal({
  conflict,
  conclusion,
  onConclusionChange,
  reason,
  onReasonChange,
  pending,
  error,
  onConfirm,
  onClose,
}: {
  conflict: FingerprintConflict;
  conclusion: FingerprintConflictConclusion;
  onConclusionChange: (value: FingerprintConflictConclusion) => void;
  reason: string;
  onReasonChange: (value: string) => void;
  pending: boolean;
  error: string | null;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation("opsSuppliers");
  const info = buildConflictView(conflict);
  const pendingText = t("conflicts.pending");
  const submitterValue = [info.openedByName, info.submitter].filter(Boolean).join(" · ");
  const submitterSites =
    info.submitterSites !== null
      ? info.submitterSites.length > 0
        ? info.submitterSites.join(", ")
        : t("conflicts.uploadOnly")
      : null;
  const conflictingSites =
    info.conflictingSites.length > 0
      ? info.conflictingSites.join(", ")
      : info.conflictingKeys.length === 0
        ? t("conflicts.noConflictingKeys")
        : pendingText;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-xl border bg-card p-5 shadow-lg"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-base font-semibold">{t("conflicts.resolveTitle")}</h2>
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs text-muted-foreground">{conflict.conflict_ref}</span>
            <Badge variant={conflict.status === "open" ? "warning" : "success"}>
              {t(`conflicts.${conflict.status}`)}
            </Badge>
          </div>
        </div>

        <p className="mt-4 text-xs text-muted-foreground">
          <span className="text-foreground">{t("conflicts.whatTitle")}:</span>{" "}
          {t("conflicts.what", { submitter: info.submitter, conflicting: info.conflicting })}
        </p>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <Fact label={t("conflicts.submitter")} ok value={submitterValue} />
          <Fact
            label={t("conflicts.submittedKey")}
            ok={info.submittedKey !== null}
            value={info.submittedKey ?? pendingText}
            hint={info.submittedKey === null ? t("conflicts.hintKey") : undefined}
          />
          <Fact
            label={t("conflicts.submitterSite")}
            ok={submitterSites !== null}
            value={submitterSites ?? pendingText}
            hint={submitterSites === null ? t("conflicts.hintSite") : undefined}
          />
          <Fact label={t("conflicts.conflicting")} ok value={info.conflicting} />
          <Fact
            label={t("conflicts.conflictingSites")}
            ok={info.conflictingKeys.length > 0}
            value={conflictingSites}
            hint={info.conflictingKeys.length === 0 ? t("conflicts.hintSites") : undefined}
          />
        </div>
        <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
          <span>{t("conflicts.fingerprint")}</span>
          <span className="font-mono">{info.fingerprint}</span>
        </div>

        {info.conflictingKeys.length > 0 && (
          <div className="mt-3 rounded-lg border px-3 py-2.5 text-xs">
            <div className="font-medium">{t("conflicts.existingKeys")}</div>
            <ul className="mt-1.5 space-y-1.5">
              {info.conflictingKeys.map((k) => (
                <li key={k.key_id} className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                  <span className="font-mono">{k.name}</span>
                  <span className="font-mono text-muted-foreground">{k.owner}</span>
                  {k.sites && k.sites.length > 0 && (
                    <span className="text-muted-foreground">
                      {t("conflicts.sites")}: {k.sites.join(", ")}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {info.submittedKey === null && (
          <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-900">
            <strong>{t("conflicts.gapTitle")}</strong>
            <ul className="mt-1 list-disc space-y-1 pl-4">
              <li>{t("conflicts.gapItem1")}</li>
            </ul>
          </div>
        )}

        <div className="mt-4 space-y-4">
          <div className="space-y-1">
            <Label>{t("conflicts.conclusion")}</Label>
            <select
              className="h-10 w-full rounded-md border bg-background px-3 text-sm"
              value={conclusion}
              onChange={(event) => onConclusionChange(event.target.value as FingerprintConflictConclusion)}
            >
              {conclusions.map((value) => (
                <option key={value} value={value}>{t(`conflicts.conclusions.${value}`)}</option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="conflict-reason">{t("conflicts.reason")}</Label>
            <textarea
              id="conflict-reason"
              className="min-h-24 w-full rounded-md border bg-background px-3 py-2 text-sm"
              maxLength={1000}
              value={reason}
              onChange={(event) => onReasonChange(event.target.value)}
            />
          </div>
        </div>

        {error && (
          <div className="mt-3 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>{t("cancel")}</Button>
          <Button disabled={!reason.trim() || pending} onClick={onConfirm}>
            {pending ? "…" : t("conflicts.confirmResolve")}
          </Button>
        </div>
      </div>
    </div>
  );
}
